import { ComponentType, PropsWithChildren, useReducer } from "react";
import * as React from "react";

export enum ProjectionProviderStatus {
  Ready = "ready",
  CanSubmit = "canSubmit",
  Creating = "creating",
  Finished = "finished",
  Error = "error",
  Initializing = "initializing"
}

export enum ProjectionProviderIdentifier {
  NPI = "npi",
  TIN = "tin",
  NPITIN = "npitin",
  None = "",
}

export interface ProjectionProviderDetailsForm {
  allLocations: boolean;
  allProjects: boolean;
  allSpecialties: boolean;
  identifier: ProjectionProviderIdentifier;
  reasonDescription: string;
  reasonName: string;
  requireReRun: boolean;
  selectedExclusionVerifiedSources: string[];
  status: ProjectionProviderStatus;
  maxLength: number;
  disableRerunPreview: boolean;
}

export interface ProjectionProviderDetailsProps {
  formState: ProjectionProviderDetailsForm;
  formDispatch: React.Dispatch<ProjectionProviderDetailsAction>;
}

export enum ProjectionProviderDetailsActionType {
  AllLocations = "allLocations",
  AllProjects = "allProjects",
  AllSpecialties = "allSpecialties",
  Identifier = "identifier",
  ReasonDescription = "reasonDescription",
  ReasonName = "reasonName",
  SelectedExclusionVerifiedSources = "selectedExclusionVerifiedSources",
  Finished = "finished",
  Initialize = "initialize",
  RunPreview = "runPreview",
  Reset = "reset"
}

export const projectionProviderDetailsInitialState: ProjectionProviderDetailsForm = {
  allLocations: false,
  allProjects: false,
  allSpecialties: false,
  disableRerunPreview: true,
  identifier: ProjectionProviderIdentifier.None,
  maxLength: 1000,
  reasonDescription: "",
  reasonName: "",
  requireReRun: false,
  selectedExclusionVerifiedSources: [],
  status: ProjectionProviderStatus.Initializing,
}

export type ProjectionProviderDetailsAction =
  { type: ProjectionProviderDetailsActionType.AllLocations, payload: boolean }
  | { type: ProjectionProviderDetailsActionType.AllProjects, payload: boolean }
  | { type: ProjectionProviderDetailsActionType.AllSpecialties, payload: boolean }
  | { type: ProjectionProviderDetailsActionType.Identifier, payload: ProjectionProviderIdentifier }
  | { type: ProjectionProviderDetailsActionType.ReasonDescription, payload: string }
  | { type: ProjectionProviderDetailsActionType.ReasonName, payload: string }
  | { type: ProjectionProviderDetailsActionType.SelectedExclusionVerifiedSources, payload: string[] }
  | { type: ProjectionProviderDetailsActionType.Finished }
  | { type: ProjectionProviderDetailsActionType.RunPreview }
  | { type: ProjectionProviderDetailsActionType.Reset }
  | { type: ProjectionProviderDetailsActionType.Initialize, payload: ProjectionProviderDetailsForm };

const projectionProviderDetailsReducer = (state: ProjectionProviderDetailsForm, action: ProjectionProviderDetailsAction): ProjectionProviderDetailsForm => {
  switch (action.type) {
    case ProjectionProviderDetailsActionType.Initialize:
      return {
        ...state,
        allLocations: action.payload.allLocations,
        allProjects: action.payload.allProjects,
        allSpecialties: action.payload.allSpecialties,
        disableRerunPreview: true,
        identifier: action.payload.identifier,
        maxLength: action.payload.maxLength,
        reasonDescription: action.payload.reasonDescription,
        reasonName: action.payload.reasonName,
        requireReRun: action.payload.requireReRun,
        selectedExclusionVerifiedSources: action.payload.selectedExclusionVerifiedSources,
        status: action.payload.status
      };
    case ProjectionProviderDetailsActionType.Reset:
      return {
        ...projectionProviderDetailsInitialState,
        status: ProjectionProviderStatus.Initializing
      };
    case ProjectionProviderDetailsActionType.AllLocations:
      return {
        ...state,
        allLocations: action.payload,
        disableRerunPreview: false,
        requireReRun: true,
        status: getStatus(state.reasonDescription, state.reasonName, state.maxLength)
      };
    case ProjectionProviderDetailsActionType.AllProjects:
      return {
        ...state,
        allProjects: action.payload,
        requireReRun: true,
        status: getStatus(state.reasonDescription, state.reasonName, state.maxLength)
      };
    case ProjectionProviderDetailsActionType.AllSpecialties:
      return {
        ...state,
        allSpecialties: action.payload,
        disableRerunPreview: false,
        requireReRun: true,
        status: getStatus(state.reasonDescription, state.reasonName, state.maxLength)
      };
    case ProjectionProviderDetailsActionType.Identifier:
      return {
        ...state,
        disableRerunPreview: false,
        identifier: action.payload
      }
    case ProjectionProviderDetailsActionType.ReasonDescription:
      return {
        ...state,
        reasonDescription: action.payload,
        status: getStatus(action.payload, state.reasonName, state.maxLength)
      }
    case ProjectionProviderDetailsActionType.ReasonName:
      return {
        ...state,
        reasonName: action.payload,
        status: getStatus(state.reasonDescription, action.payload, state.maxLength)
      }
    case ProjectionProviderDetailsActionType.SelectedExclusionVerifiedSources:
      return {
        ...state,
        selectedExclusionVerifiedSources: action.payload,
        status: getStatus(state.reasonDescription, state.reasonName, state.maxLength)
      }
    case ProjectionProviderDetailsActionType.Finished: {
      return {
        ...state,
        reasonDescription: state.reasonDescription.slice(0, state.maxLength),
        status: ProjectionProviderStatus.Finished
      }
    }
    case ProjectionProviderDetailsActionType.RunPreview: {
      return {
        ...state,
        disableRerunPreview: true
      }
    }
    default:
      return state;
  }
}

const getStatus = (reasonDescription: string, reasonName: string, maxLength: number): ProjectionProviderStatus => {
  return !!reasonName && (reasonName !== "Other" || (reasonDescription && reasonDescription.length > 0 && reasonDescription.length < maxLength)) ? ProjectionProviderStatus.CanSubmit : ProjectionProviderStatus.Ready;
}

export default projectionProviderDetailsReducer;

export function withProjectionProviderDetailsReducer<T>(Component: ComponentType<PropsWithChildren<T & ProjectionProviderDetailsProps>>): ComponentType<PropsWithChildren<T>> {
  return function WrappedComponent(props) {
    const [projectionProvidersState, projectionProvidersReducer] = useReducer(projectionProviderDetailsReducer, projectionProviderDetailsInitialState);
    return <Component {...props} formState={projectionProvidersState} formDispatch={projectionProvidersReducer}/>;
  }
}
